import {
    ComponentRef,
    createComponent,
    EnvironmentInjector,
    EventEmitter,
    Injector,
    InputSignal,
    OutputEmitterRef,
    signal,
    Type,
    ViewContainerRef
} from '@angular/core';
import {concat, EMPTY, ignoreElements, Observable, of, ReplaySubject, Subject, Subscription, takeUntil} from 'rxjs';
import {share} from 'rxjs/operators';

/** Value for a single component property in DynamicComponentConfig */
type ComponentConfigValue<V> =
    V extends EventEmitter<infer E> ? (event: E) => void :          // @Output() x = new EventEmitter<E>()
        V extends OutputEmitterRef<infer E> ? (event: E) => void :  // x = output<E>()
            V extends InputSignal<infer T> ? T :                    // x = input<T>() - value, not a signal
                V;                                                  // regular @Input()

/** Configuration: input values and/or output handlers in a single object */
export type DynamicComponentConfig<C> = {
    [K in keyof C]?: ComponentConfigValue<C[K]>;
};

/** Duck-type: EventEmitter, OutputEmitterRef, Subject, любой rxjs-subscribable */
function isSubscribable(value: unknown): value is { subscribe(handler: (...args: any[]) => void): any } {
    return !!value && typeof (value as { subscribe?: unknown }).subscribe === 'function';
}

/**
 * Reference to a dynamically created component.
 *
 * Lifecycle:
 *   createComponent() -> insertTo()/detach() (any number of times) -> destroy()
 *
 * The component is created "detached" - the caller decides where to insert it:
 * ViewContainerRef, DOM node, or ApplicationRef. If the view is destroyed by the
 * ViewContainerRef itself (clear()/remove()), the ref learns about it and completes destroy$.
 */
export class DynamicComponentRef<out C> {

    public readonly componentClass: Type<C>;
    public readonly injector: Injector;
    public readonly environmentInjector: EnvironmentInjector;

    public componentRef: ComponentRef<C> | null = null;
    public viewContainerRef: ViewContainerRef | null = null;

    /** true after destruction (including when initiated by ViewContainerRef) */
    public readonly destroyed = signal(false);

    /** Emits once and completes when the component is destroyed. */
    public readonly destroy$: Observable<void>;

    private readonly destroySubject = new Subject<void>();
    private refSharedObservable?: Observable<DynamicComponentRef<C>>;

    /** Subscriptions to outputs by key; reconfiguring a key replaces the subscription */
    private readonly outputSubscriptions = new Map<string, Subscription>();

    constructor(
        componentClass: Type<C>,
        injector: Injector,
        environmentInjector?: EnvironmentInjector
    ) {
        this.componentClass = componentClass;
        this.injector = injector;

        // createComponent() requires an environmentInjector: without it standalone
        // components (the default in Angular 20) fail at runtime. An explicit
        // argument takes priority; otherwise we get the token from the provided
        // injector - the node injector (vcr.injector) falls through to the
        // environment tree.
        const envInjector = environmentInjector ?? injector.get(EnvironmentInjector, null);

        if (!envInjector) {
            throw new Error(
                'DynamicComponentRef: could not determine environmentInjector. ' +
                'Pass it as the third argument, for example inject(EnvironmentInjector).'
            );
        }
        this.environmentInjector = envInjector;

        this.destroy$ = this.destroySubject.asObservable();
    }

    public get instance(): C {
        return this.requireComponentRef().instance;
    }

    /** Creates the component (detached from DOM). Idempotent. */
    public createComponent(): this {
        if (this.componentRef) {
            return this;
        }
        if (this.destroyed()) {
            throw new Error('DynamicComponentRef: createComponent() after destroy()');
        }

        this.componentRef = createComponent(this.componentClass, {
            // DI parent of the component. Both the node injector (vcr.injector)
            // and a regular injector work, as before in factory.create().
            elementInjector:     this.injector,
            environmentInjector: this.environmentInjector
        });

        // if the view is destroyed by the ViewContainerRef itself (clear()/remove())
        this.componentRef.onDestroy(() => this.markDestroyed());

        return this;
    }

    /**
     * Configures the created component.
     *
     * - object: routing per key:
     *   - output (EventEmitter / OutputEmitterRef / any subscribable) + function ->
     *     subscribe, the subscription is automatically removed on destroy();
     *   - anything else -> ComponentRef.setInput() (OnPush, signal input(), zoneless-safe);
     * - function: arbitrary imperative setup of the instance.
     */
    public configureComponent(config: DynamicComponentConfig<C>): this;
    public configureComponent(callback: (instance: C, componentRef: ComponentRef<C>) => void): this;
    public configureComponent(
        configOrCallback:
            | DynamicComponentConfig<C>
            | ((instance: C, componentRef: ComponentRef<C>) => void)
    ): this {
        const componentRef = this.requireComponentRef();

        if (typeof configOrCallback === 'function') {
            configOrCallback(componentRef.instance, componentRef);
            return this;
        }

        const instance = componentRef.instance as Record<string, unknown>;

        for (const key of Object.keys(configOrCallback) as Array<keyof C & string>) {
            const value = configOrCallback[key];
            const current = instance[key];

            // TS cannot narrow generic indexed access to a function signature
            // (you get "... & Function", an interface without a call). We type the
            // handler explicitly; the runtime check below is still in place.
            const handler = value as ((...args: unknown[]) => void) | undefined;

            if (typeof handler === 'function' && isSubscribable(current)) {
                this.outputSubscriptions.get(key)?.unsubscribe();   // previous handler removed

                const subscription = new Subscription();
                // EventEmitter/Subject return a Subscription, OutputEmitterRef returns
                // an unsubscribe function; Subscription.add accepts both
                subscription.add(current.subscribe(handler));
                this.outputSubscriptions.set(key, subscription);
            } else {
                componentRef.setInput(key, value);
            }
        }

        return this;
    }

    /** Detaches the view from the current ViewContainerRef; the view stays alive. */
    public detach(): this {
        const viewContainerRef = this.viewContainerRef;
        const componentRef = this.componentRef;

        if (viewContainerRef && componentRef) {
            const index = viewContainerRef.indexOf(componentRef.hostView);
            if (index >= 0) {
                viewContainerRef.detach(index);
            }
            this.viewContainerRef = null;
        }
        return this;
    }

    /** Inserts the component into a ViewContainerRef (at the end or at an index). */
    public insertTo(viewContainerRef: ViewContainerRef, index?: number): this {
        const componentRef = this.requireComponentRef();

        this.detach();
        viewContainerRef.insert(componentRef.hostView, index);
        this.viewContainerRef = viewContainerRef;

        return this;
    }

    public insertBefore(reference: DynamicComponentRef<unknown>): this {
        const viewContainerRef = reference.viewContainerRef;
        const index = reference.getIndex();
        if (viewContainerRef && index >= 0) {
            this.insertTo(viewContainerRef, index);
        }
        return this;
    }

    public insertAfter(reference: DynamicComponentRef<unknown>): this {
        const viewContainerRef = reference.viewContainerRef;
        const index = reference.getIndex();
        if (viewContainerRef && index >= 0) {
            this.insertTo(viewContainerRef, index + 1);
        }
        return this;
    }

    public getIndex(): number {
        return this.viewContainerRef && this.componentRef
            ? this.viewContainerRef.indexOf(this.componentRef.hostView)
            : -1;
    }

    /** Destroys the component; a second call is a no-op. */
    public destroy(): void {
        if (this.destroyed()) {
            return;
        }
        this.markDestroyed();          // destroy$ emits before the physical destroy:
        this.componentRef?.destroy();  // subscribers can still read the instance
    }

    /** Registers a callback for destruction. */
    public onDestroy(callback: () => void): this {
        this.destroy$.subscribe(callback);
        return this;
    }

    /** Destroys the component on the first emission of trigger$. */
    public destroyWhen(trigger$: Observable<unknown>): this {
        trigger$.pipe(takeUntil(this.destroy$)).subscribe(() => this.destroy());
        return this;
    }

    /**
     * Every subscriber synchronously receives the ref; the stream completes on
     * destroy(). After destroy, a subscription receives the ref and immediately
     * completes.
     *
     * Note: unsubscribing the last subscriber kills the component.
     */
    public asObservable(): Observable<DynamicComponentRef<C>> {

        // One instance per ref
        return (this.refSharedObservable ??= concat(
            of(this),
            this.destroy$.pipe(ignoreElements())
        ).pipe(
            share({
                // Replay inside the "warm" cycle: the 2nd and subsequent
                // subscribers synchronously receive the ref
                connector: () => new ReplaySubject<DynamicComponentRef<C>>(1),

                // Called at the moment refCount -> 0. The side effect is here;
                // the returned value is a notifier that resets state.
                resetOnRefCountZero: () => {
                    this.destroy();

                    return EMPTY;
                }
            })
        ));
    }

    private markDestroyed(): void {
        if (this.destroyed()) {
            return;
        }

        this.destroyed.set(true);

        for (const subscription of this.outputSubscriptions.values()) {
            subscription.unsubscribe();
        }
        this.outputSubscriptions.clear();

        this.destroySubject.next();
        this.destroySubject.complete();
    }

    private requireComponentRef(): ComponentRef<C> {
        if (this.destroyed()) {
            throw new Error('DynamicComponentRef: component is already destroyed');
        }
        if (!this.componentRef) {
            throw new Error('DynamicComponentRef: call createComponent() first');
        }
        return this.componentRef;
    }
}
